import * as React from 'react';
import styled from 'styled-components';
import { Loader } from '../atoms';
import { getUsersCustom } from '../api/users';
import { USER_TYPES_VALUES } from '../constants/forms';

interface Contact {
  id: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  companyName?: string;
  parentCompany?: { companyName?: string } | number | null;
}

interface Props {
  searchQuery: string;
  selectedIds: number[];
  onToggle: (contact: Contact) => void;
  limit?: number;
}

const ContactSearchList = ({
  searchQuery,
  selectedIds,
  onToggle,
  limit = 25,
}: Props) => {
  const [results, setResults] = React.useState<Contact[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasSearched, setHasSearched] = React.useState(false);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastQueryRef = React.useRef<string>('');

  React.useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    const trimmed = (searchQuery || '').trim();
    if (trimmed.length < 2) {
      setResults([]);
      setHasSearched(false);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    debounceRef.current = setTimeout(() => {
      lastQueryRef.current = trimmed;
      getUsersCustom({
        limit,
        userRole: USER_TYPES_VALUES.CLIENT,
        search: trimmed,
      })
        .then(res => {
          if (lastQueryRef.current !== trimmed) return;
          const rows =
            (res?.data as any)?.results || (res?.data as any) || [];
          setResults(rows as Contact[]);
          setHasSearched(true);
        })
        .catch(() => {
          setResults([]);
          setHasSearched(true);
        })
        .finally(() => {
          if (lastQueryRef.current === trimmed) {
            setIsLoading(false);
          }
        });
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery, limit]);

  if (isLoading) {
    return (
      <LoaderWrap>
        <Loader size="36px" />
      </LoaderWrap>
    );
  }

  if (hasSearched && results.length === 0) {
    return (
      <EmptyState>
        No contacts match &quot;{searchQuery}&quot;.
        <EmptyStateHint>
          Try a different name or email, or click <strong>New</strong> to add
          a brand-new contact.
        </EmptyStateHint>
      </EmptyState>
    );
  }

  return (
    <List>
      {results.map(contact => {
        const isSelected = selectedIds.includes(contact.id);
        const name = [contact.firstName, contact.lastName]
          .filter(Boolean)
          .join(' ');
        const parentCompanyName =
          contact.parentCompany &&
          typeof contact.parentCompany === 'object'
            ? contact.parentCompany.companyName
            : '';
        const company = contact.companyName || parentCompanyName || '';
        return (
          <Row
            key={contact.id}
            $selected={isSelected}
            onClick={() => onToggle(contact)}
            type="button"
          >
            <Checkbox $checked={isSelected}>{isSelected ? '✓' : ''}</Checkbox>
            <Info>
              <Name>{name || contact.companyName || contact.email || 'Unnamed'}</Name>
              <DetailLine>
                {contact.email && <DetailItem>{contact.email}</DetailItem>}
                {company && (
                  <>
                    {contact.email && <DetailDot>·</DetailDot>}
                    <DetailItem>{company}</DetailItem>
                  </>
                )}
              </DetailLine>
            </Info>
          </Row>
        );
      })}
    </List>
  );
};

const LoaderWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 28px 16px;
  font-size: 0.875rem;
  color: var(--neutral-600);
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 10px;
  margin-top: 12px;
`;

const EmptyStateHint = styled.div`
  margin-top: 6px;
  font-size: 0.8125rem;
  color: var(--neutral-500);

  strong {
    color: var(--primary-700);
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
  max-height: 50vh;
  overflow-y: auto;
  padding: 2px;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--neutral-300);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: var(--neutral-400);
  }
`;

const Row = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid
    ${({ $selected }) =>
      $selected ? 'var(--primary-600)' : 'var(--border)'};
  background: ${({ $selected }) =>
    $selected ? 'var(--primary-50)' : 'var(--neutral-0)'};
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  transition: border-color 0.15s ease, background-color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    border-color: var(--primary-600);
    background: ${({ $selected }) =>
      $selected ? 'var(--primary-100)' : 'var(--neutral-50)'};
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const Checkbox = styled.div<{ $checked: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 2px solid
    ${({ $checked }) =>
      $checked ? 'var(--primary-600)' : 'var(--neutral-400)'};
  background: ${({ $checked }) =>
    $checked ? 'var(--primary-600)' : 'transparent'};
  color: var(--neutral-0);
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  transition: border-color 0.15s ease, background-color 0.15s ease;
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const Name = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--mainText);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DetailLine = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 0.8125rem;
  color: var(--neutral-500);
  min-width: 0;
`;

const DetailItem = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DetailDot = styled.span`
  color: var(--neutral-400);
`;

export default ContactSearchList;
